// pages/JSONTool.tsx
import React, { useState } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/ext-searchbox";

// ✅ Import only what's needed — do NOT import the worker
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/ext-language_tools";
export default function JSONTool() {
  const [jsonInput, setJsonInput] = useState("");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");

  const validateJSON = () => {
    try {
      JSON.parse(jsonInput);
      setError("");
      setOutput("✅ Valid JSON");
    } catch (err) {
      setOutput("");
      setError(`❌ ${err.message}`);
    }
  };

  const beautifyJSON = () => {
    try {
      const obj = JSON.parse(jsonInput);
      const pretty = JSON.stringify(obj, null, 2);
      setJsonInput(pretty);
      setError("");
      setOutput("✅ Beautified");
    } catch (err) {
      setOutput("");
      setError(`❌ ${err.message}`);
    }
  };

  const minifyJSON = () => {
    try {
      const obj = JSON.parse(jsonInput);
      const minified = JSON.stringify(obj);
      setJsonInput(minified);
      setError("");
      setOutput("✅ Minified");
    } catch (err) {
      setOutput("");
      setError(`❌ ${err.message}`);
    }
  };

  return (
    <div style={{ padding: "0.5rem", fontFamily: "Arial" }}>
      <h2>JSON Validator & Beautifier</h2>

      <div className="container mt-3 p-3 bg-light rounded shadow">
        <AceEditor
          mode="json"
          theme="monokai"
          name="json_editor"
          value={jsonInput}
          onChange={setJsonInput}
          fontSize={14}
          width="100%"
          height="400px"
          showPrintMargin={true}
          showGutter={true}
          highlightActiveLine={true}
          useWorker={false}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            useWorker: false,
            showLineNumbers: true,
            tabSize: 2,
          }}
        />

        <div className="mt-4 d-flex gap-2">
          <button onClick={validateJSON} className="btn btn-primary">
            Validate
          </button>
          <button onClick={beautifyJSON} className="btn btn-success">
            Beautify
          </button>
          <button onClick={minifyJSON} className="btn btn-secondary">
            Minify
          </button>
        </div>

        <div style={{ marginTop: "1rem" }}>
          {output && <p className="text-success">{output}</p>}
          {error && <p className="text-danger">{error}</p>}
        </div>
      </div>

      <div className="container mt-5 p-4 bg-white rounded shadow-sm border">
        <h1 className="h3 text-center text-dark">About dJSONLinter</h1>
        <p className="mt-3 text-muted">
          dJSONLinter is an intuitive and powerful tool designed to help you
          validate and beautify your JSON data. Whether you're dealing with a
          small snippet or a complex JSON file, dJSONLinter ensures that your
          code is correctly formatted and error-free. Simply copy and paste, or
          directly type your JSON, and dJSONLinter will tidy it up, making it
          easy to work with.
        </p>

        <h2 className="h4 mt-4 text-dark">Features</h2>
        <ul className="mt-2 ps-4 text-muted">
          <li>
            <strong>Instant Validation:</strong> Quickly check the validity of
            your JSON data with real-time error highlighting.
          </li>
          <li>
            <strong>Reformatting:</strong> Beautify your JSON data by adding
            proper indentation for better readability.
          </li>
          <li>
            <strong>Compression:</strong> Reduce JSON file size by using our
            compression feature, which removes unnecessary spaces and newlines.
          </li>
        </ul>

        {/* <h2 className="h4 mt-4 text-dark">Tips & Tricks</h2>
        <p className="text-muted">
          Want to compress your JSON file? Add{" "}
          <span className="text-success">?reformat=compress</span> to the URL,
          and it will return the compressed version of your JSON data.
        </p> */}

        <h2 className="h4 mt-4 text-dark">Common Issues and Fixes</h2>
        <p className="text-muted">
          <strong>Extra Comma:</strong> The error{" "}
          <code className="text-danger">Expecting 'STRING'</code> often occurs
          when there's an extra comma at the end of your JSON object or array.
          Example:
        </p>
        <pre>
          <code>{`{
  "name": "John",
}`}</code>
        </pre>

        <p>
          <strong className="app">Unexpected Tokens:</strong> If you see an
          error like{" "}
          <code className="text-danger">Expecting 'STRING', 'NUMBER'</code>, you
          might have an extra comma at the end of a list. Example:
        </p>
        <pre>
          <code>{`["a", "b", ]`}</code>
        </pre>

        <p>
          <strong>Keys Not Quoted:</strong> Make sure all collection keys are
          properly quoted. Example of correct JSON format:
        </p>
        <pre>
          <code>
            {`{
  "name": "value"
}`}
          </code>
        </pre>

        <h2 className="h4 mt-4 text-dark">Troubleshooting Newlines</h2>
        <p className="text-muted">
          If you're working on a <strong>Windows</strong> computer, you might
          experience issues with newline characters (
          <code className="text-danger">\n</code>). Windows typically requires a
          carriage return (<code className="text-danger">\r\n</code>) for
          newlines. If you encounter validation issues with newlines, you can
          ensure your content matches the newline format expected by the
          platform.
        </p>
      </div>
    </div>
  );
}
